import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, X, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inp, money } from "./inputs";

const APP_STATUSES = [["submitted", "Submitted"], ["under_review", "Under Review"], ["approved", "Approved"], ["waitlisted", "Waitlisted"], ["info_needed", "More Info Needed"], ["declined", "Declined"]];

const StatusSelect = ({ value, onChange, testid }) => (
  <select className={`${inp} w-auto`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {APP_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const PeopleTab = () => {
  const [view, setView] = useState("members");
  const [q, setQ] = useState("");
  const [members, setMembers] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [minApps, setMinApps] = useState(null);
  const [memApps, setMemApps] = useState(null);

  const loadMembers = useCallback(async () => {
    try { const { data } = await api.get("/admin/members", { params: { q: q || undefined } }); setMembers(data); }
    catch { toast.error("Failed to load members"); }
  }, [q]);
  useEffect(() => { const t = setTimeout(loadMembers, 300); return () => clearTimeout(t); }, [loadMembers]);
  useEffect(() => {
    api.get("/admin/member-statuses").then((r) => setStatuses(r.data.statuses)).catch(() => {});
    api.get("/admin/ministry-applications").then((r) => setMinApps(r.data)).catch(() => setMinApps([]));
    api.get("/admin/membership-applications").then((r) => setMemApps(r.data)).catch(() => setMemApps([]));
  }, []);

  const openDetail = async (m) => {
    try { const { data } = await api.get(`/admin/members/${m.id}`); setDetail(data); }
    catch { toast.error("Failed to load member"); }
  };
  const setStatus = async (mid, status) => {
    try {
      const { data } = await api.put(`/admin/members/${mid}/status`, { membership_status: status });
      toast.success(`Status updated to ${status}`);
      setDetail((d) => (d ? { ...d, member: data } : d));
      loadMembers();
    } catch { toast.error("Update failed"); }
  };
  const setMinAppStatus = async (aid, status) => {
    try {
      await api.put(`/admin/ministry-applications/${aid}`, { status });
      toast.success("Application updated — member notified by email");
      const { data } = await api.get("/admin/ministry-applications"); setMinApps(data);
    } catch { toast.error("Update failed"); }
  };
  const setMemAppStatus = async (aid, status) => {
    try {
      await api.put(`/admin/membership-applications/${aid}`, { status });
      toast.success(status === "approved" ? "Approved! Member is now an Active Member" : "Application updated");
      const { data } = await api.get("/admin/membership-applications"); setMemApps(data);
      loadMembers();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="people-tab">
      <h1 className="font-display font-bold text-2xl mb-6">People / Members</h1>
      <div className="flex gap-2 mb-6 flex-wrap">
        {[["members", `Members${members ? ` (${members.length})` : ""}`],
          ["ministry", `Ministry Applications${minApps ? ` (${minApps.filter((a) => ["submitted", "under_review"].includes(a.status)).length})` : ""}`],
          ["membership", `Membership Applications${memApps ? ` (${memApps.filter((a) => ["submitted", "under_review"].includes(a.status)).length})` : ""}`]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`people-view-${v}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${view === v ? "bg-white text-[#0A0A0A]" : "border border-white/15 text-white/60 hover:border-white/40"}`}>{l}</button>
        ))}
      </div>

      {view === "members" && (
        <>
          <div className="relative mb-4">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input className={`${inp} pl-8`} placeholder="Search by name, email, phone, member ID or status" data-testid="members-search-input" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {!members ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
            <div className="space-y-2">
              {members.map((m) => (
                <button key={m.id} onClick={() => openDetail(m)} data-testid="member-row"
                  className="w-full flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-lg p-3 text-left hover:border-white/25">
                  <div className="w-10 h-10 rounded-full bg-[#E63946]/15 grid place-items-center font-display font-bold text-[#E63946] shrink-0">{(m.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{m.name} {!m.email_verified && <span className="text-xs text-yellow-400 font-normal ml-1">(unverified)</span>}</p>
                    <p className="text-sm text-white/40 truncate">{m.email} · <span className="font-mono">{m.member_id}</span></p>
                  </div>
                  <span className="text-xs px-3 py-1.5 rounded-full bg-white/10 text-white/60 shrink-0">{m.membership_status}</span>
                </button>
              ))}
              {members.length === 0 && <p className="text-white/40 py-16 text-center">No member accounts yet.</p>}
            </div>
          )}
        </>
      )}

      {view === "ministry" && (
        !minApps ? <Loader2 className="animate-spin text-[#E63946]" /> : (
          <div className="space-y-2">
            {minApps.map((a) => (
              <div key={a.id} className="bg-white/[0.03] border border-white/10 rounded-lg p-4" data-testid="ministry-app-admin-row">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-medium">{a.member_name} <span className="text-white/40 font-normal">→ {a.ministry_name}</span></p>
                    <p className="text-sm text-white/40">{a.member_email}{a.phone ? ` · ${a.phone}` : ""} · {new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                  <StatusSelect value={a.status} onChange={(s) => setMinAppStatus(a.id, s)} testid="ministry-app-status-select" />
                </div>
                {(a.why || a.experience || a.availability) && (
                  <div className="mt-3 text-sm text-white/50 space-y-1 border-t border-white/10 pt-3">
                    {a.why && <p><span className="text-white/30">Why:</span> {a.why}</p>}
                    {a.experience && <p><span className="text-white/30">Experience:</span> {a.experience}</p>}
                    {a.availability && <p><span className="text-white/30">Availability:</span> {a.availability}</p>}
                  </div>
                )}
              </div>
            ))}
            {minApps.length === 0 && <p className="text-white/40 py-16 text-center">No ministry applications yet.</p>}
          </div>
        )
      )}

      {view === "membership" && (
        !memApps ? <Loader2 className="animate-spin text-[#E63946]" /> : (
          <div className="space-y-2">
            {memApps.map((a) => (
              <div key={a.id} className="bg-white/[0.03] border border-white/10 rounded-lg p-4" data-testid="membership-app-admin-row">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-medium">{a.name}</p>
                    <p className="text-sm text-white/40">{a.email}{a.phone ? ` · ${a.phone}` : ""} · {new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                  <StatusSelect value={a.status} onChange={(s) => setMemAppStatus(a.id, s)} testid="membership-app-status-select" />
                </div>
                <div className="mt-3 text-sm text-white/50 space-y-1 border-t border-white/10 pt-3">
                  {a.how_long && <p><span className="text-white/30">Attending for:</span> {a.how_long}</p>}
                  {a.previous_church && <p><span className="text-white/30">Previous church:</span> {a.previous_church}</p>}
                  {a.baptized && <p><span className="text-white/30">Baptized:</span> {a.baptized}</p>}
                  {a.serve_areas && <p><span className="text-white/30">Wants to serve:</span> {a.serve_areas}</p>}
                </div>
              </div>
            ))}
            {memApps.length === 0 && <p className="text-white/40 py-16 text-center">No membership applications yet.</p>}
          </div>
        )
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-[#141414] border border-white/10 rounded-2xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()} data-testid="member-detail-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="font-display font-bold text-lg">{detail.member.name}</h3>
              <button onClick={() => setDetail(null)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="text-sm space-y-1 text-white/60">
                <p>Email: {detail.member.email} {detail.member.email_verified ? "✓ verified" : "(unverified)"}</p>
                {detail.member.phone && <p>Phone: {detail.member.phone}</p>}
                {detail.member.address && <p>Address: {detail.member.address}</p>}
                <p>Member ID: <span className="font-mono">{detail.member.member_id}</span></p>
                <p>Joined portal: {new Date(detail.member.created_at).toLocaleDateString()}</p>
                <p data-testid="member-auth-methods">Sign-in methods: {[
                  (detail.member.auth_identities || []).some((i) => i.provider === "google") && "Google",
                  (detail.member.auth_identities || []).some((i) => i.provider === "apple") && "Apple",
                  "Email/Password",
                ].filter(Boolean).join(" · ")}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Membership Status</p>
                <select className={inp} value={detail.member.membership_status} data-testid="member-status-select"
                  onChange={(e) => setStatus(detail.member.id, e.target.value)}>
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/[0.04] rounded-lg p-3 text-center"><p className="font-display font-bold text-xl">{detail.orders_count}</p><p className="text-xs text-white/40">Orders</p></div>
                <div className="bg-white/[0.04] rounded-lg p-3 text-center"><p className="font-display font-bold text-xl">{detail.tickets_count}</p><p className="text-xs text-white/40">Ticket Orders</p></div>
                <div className="bg-white/[0.04] rounded-lg p-3 text-center">
                  {"giving_total" in detail ? <><p className="font-display font-bold text-xl text-[#E63946]" data-testid="member-giving-total">{money(detail.giving_total)}</p><p className="text-xs text-white/40">Giving (lifetime)</p></>
                    : <><p className="font-display font-bold text-xl text-white/25">—</p><p className="text-xs text-white/30">Giving restricted</p></>}
                </div>
              </div>
              {detail.ministry_applications.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Ministries & Applications</p>
                  {detail.ministry_applications.map((a) => (
                    <p key={a.id} className="text-sm text-white/60 py-0.5 flex items-center gap-2"><BadgeCheck size={13} className="text-[#E63946]" /> {a.ministry_name} — {a.status.replace("_", " ")}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
